import * as Headless from '@headlessui/react';
import { ArrowUturnLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@oysterjs/ui/table';
import React from 'react';
import { ChangeCell, columnWidth } from './change-cell';
import { countByFilter, matchesFilter, ROW_FILTERS, type RowFilter } from './row-filters';
import { ScrollingIntoView } from './scroll';
import { isSelectable } from '~/components/csv-import/model/selection';
import { type CellError } from '~/components/csv-import/model/validate';
import {
  changeId,
  hasNonDuplicateErrors,
  isClashing,
  isContested,
  type DiffChange,
  type DiffClassification,
  type DiffResult
} from '~/components/csv-import/model/diff';
import { type CsvSchema } from '~/components/csv-import/model/schema';

/**
 * Review of a reconciled upload, grouped by classification rather than file order: the
 * user is deciding "accept these twelve additions?", not "what is on line 41?".
 */

type Section = {
  title: string;
  /** For the skip button's label, as in "do not add it". Only selectable rows have one. */
  verb?: string;
  background: string;
};

// Key order is render order. Removals first: destructive, and the most likely to be
// approved by reflex.
const SECTIONS: Record<DiffClassification, Section> = {
  removed: { title: 'Removals', verb: 'remove', background: 'bg-red-50/70 dark:bg-red-500/10' },
  added: { title: 'Additions', verb: 'add', background: 'bg-emerald-50/50 dark:bg-emerald-500/5' },
  updated: { title: 'Updates', verb: 'update', background: 'bg-sky-50/50 dark:bg-sky-500/5' },
  unchanged: { title: 'Duplicates', background: 'bg-transparent' }
};

const ROW_LABEL_PLAIN = 'text-neutral-400 dark:text-neutral-500';

// Fits "Conflicts with 3, 4" and "Duplicate of 12"; longer labels are cut off.
const ROW_WIDTH = 'w-[10rem] min-w-[10rem] max-w-[10rem]';

/** What the Row column says about a change: its line, or why it stands out. */
const rowLabelFor = (
  change: DiffChange,
  included: boolean,
  result: DiffResult
): { text: React.ReactNode; className: string } => {
  if (change.line === undefined) {
    return included
      ? { text: 'Will delete', className: 'font-medium text-red-700 dark:text-red-300' }
      : // Inline-block so the row's strikethrough skips it.
        { text: <span className="inline-block">Kept</span>, className: ROW_LABEL_PLAIN };
  }
  if (change.repeatOf !== undefined) {
    return { text: `Duplicate of ${change.repeatOf}`, className: ROW_LABEL_PLAIN };
  }
  if (change.classification === 'unchanged') {
    return { text: 'Already in list', className: ROW_LABEL_PLAIN };
  }
  if (isClashing(change, result)) {
    return {
      text: `Conflicts with ${(change.contestedBy ?? []).join(', ')}`,
      className: ROW_LABEL_PLAIN
    };
  }
  return { text: change.line, className: ROW_LABEL_PLAIN };
};

/** Validation messages by field, so a cell can carry its own reason. */
const errorsByKey = (errors: readonly CellError[]): Record<string, string[]> => {
  const byKey: Record<string, string[]> = {};
  for (const error of errors) {
    (byKey[error.key] ??= []).push(error.message);
  }
  return byKey;
};

export const CsvReconcileTable = ({
  schema,
  result,
  selected,
  onSelectedChange,
  failures,
  disabled,
  onCellChange,
  onCellLeave,
  upToDateMessage = 'This file matches what is already here. There is nothing to apply.'
}: {
  schema: CsvSchema;
  result: DiffResult;
  selected: ReadonlySet<string>;
  onSelectedChange: (next: Set<string>) => void;
  /** Messages from a commit, by changeId, shown against the rows that failed. */
  failures?: Record<string, string>;
  /** Locks selection, filters and cell editing while a commit is in flight. */
  disabled?: boolean;
  /** Called on each keystroke, by file line. Without it, cells are read-only. */
  onCellChange?: (line: number, key: string, value: string) => void;
  /** Called when an edited cell is left; the file is re-checked then. */
  onCellLeave?: () => void;
  /** Shown instead of the table when every row is already in the list. */
  upToDateMessage?: string;
}) => {
  const scrollingIntoView = React.useRef(false);
  const latest = React.useRef({ onCellChange, onCellLeave });
  latest.current = { onCellChange, onCellLeave };
  const editCell = React.useCallback(
    (line: number, key: string, value: string) => latest.current.onCellChange?.(line, key, value),
    []
  );
  const leaveCell = React.useCallback(() => latest.current.onCellLeave?.(), []);
  // Both forms memoised so a cell's messages keep their identity between renders.
  const errorsOf = React.useMemo(
    () =>
      new Map(
        result.changes.map((change) => [
          change,
          {
            all: errorsByKey(change.errors ?? []),
            own: errorsByKey((change.errors ?? []).filter((error) => !error.duplicate))
          }
        ])
      ),
    [result]
  );
  const counts = countByFilter(result);

  const contestedCount = result.changes.filter((change) =>
    isContested(change, selected, result)
  ).length;
  const tieBreaker = schema.fields.find((field) => field.key === schema.tieBreaker);

  // Stays on an emptied filter, so fixing the last error does not snap back to All.
  const [filter, setFilter] = React.useState<RowFilter>('all');

  // Filtering hides rows rather than unmounting them; remounting cells made it lag.
  const visible = new Set(result.changes.filter((change) => matchesFilter(filter, change, result)));
  const grouped = (Object.keys(SECTIONS) as DiffClassification[])
    .map((classification) => ({
      classification,
      section: SECTIONS[classification],
      changes: result.changes.filter((change) => change.classification === classification)
    }))
    .filter((group) => group.changes.length > 0);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (!next.delete(id)) {
      next.add(id);
    }
    onSelectedChange(next);
  };

  // Unchanged rows are in the diff but offer nothing to decide.
  if (!result.changes.some(isSelectable)) {
    return (
      <p className="py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
        {upToDateMessage}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {counts.errors > 0 && (
        <p className="text-sm text-neutral-700 dark:text-neutral-200">
          <span className="font-semibold text-red-700 dark:text-red-400">
            {counts.errors} {counts.errors === 1 ? 'row has' : 'rows have'} errors
          </span>
          . Fix them below, or skip those rows, before importing.
        </p>
      )}

      {contestedCount > 0 && (
        <p className="text-sm text-neutral-700 dark:text-neutral-200">
          <span className="font-semibold text-amber-700 dark:text-amber-400">
            {contestedCount} {contestedCount === 1 ? 'row conflicts' : 'rows conflict'}
          </span>
          . {contestedCount === 1 ? 'It describes' : 'They describe'} the same entry differently.
          Skip the ones you don't want
          {tieBreaker
            ? `, or change the ${tieBreaker.label.toLowerCase()} on one of them to keep them all`
            : ''}
          .
        </p>
      )}

      <div role="group" aria-label="Show rows" className="flex flex-wrap items-center gap-2">
        {ROW_FILTERS.filter(({ key }) => key === 'all' || key === filter || counts[key] > 0).map(
          ({ key, label }) => (
            <button
              key={key}
              type="button"
              aria-pressed={filter === key}
              disabled={disabled}
              onClick={() => setFilter(key)}
              className={`rounded-full px-3.5 py-1.5 text-sm font-medium ring-1 ring-inset disabled:opacity-50 ${
                filter === key
                  ? 'bg-neutral-900 text-white ring-neutral-900 dark:bg-white dark:text-neutral-900 dark:ring-white'
                  : 'bg-white text-neutral-700 ring-neutral-200 hover:bg-neutral-50 dark:bg-neutral-800 dark:text-neutral-200 dark:ring-neutral-700 dark:hover:bg-neutral-700'
              }`}
            >
              {label} <span className="tabular-nums opacity-70">{counts[key]}</span>
            </button>
          )
        )}
      </div>

      {/* Removals have no heading, so a deletion is not left to a row tint alone. */}
      {[...visible].some((change) => change.classification === 'removed') && (
        <p className="text-xs font-medium text-red-700 dark:text-red-300">
          Rows marked “Will delete” are records not in this file. Applying deletes them.
        </p>
      )}

      {visible.size === 0 && (
        <p className="flex min-h-[19.5rem] items-center justify-center rounded-lg border border-dashed border-neutral-300 text-center text-sm text-neutral-500 dark:border-neutral-600 dark:text-neutral-400">
          No rows left here.
        </p>
      )}

      <div
        hidden={visible.size === 0}
        // Scrolls here, not in Table: `stickyHeader` leaves overflow to a parent so the
        // header sticks. Faded but still scrollable while locked.
        className={`min-h-[19.5rem] max-h-[28rem] overflow-auto overscroll-contain pb-3 rounded-lg border border-neutral-200 text-xs dark:border-neutral-700 ${
          disabled ? 'opacity-50' : ''
        }`}
      >
        <ScrollingIntoView.Provider value={scrollingIntoView}>
          <Headless.PopoverGroup>
            {/* bleed without the negative margin, so row highlights reach the border. */}
            <Table bleed dense stickyHeader className="!mx-0 [--gutter:theme(spacing.2)]">
              {/* Above the cell editor too, so a scrolling editor passes beneath it. */}
              <TableHead className="sticky top-0 z-30 bg-white dark:bg-neutral-800">
                <TableRow>
                  <TableHeader className="w-10" />
                  {schema.fields.map((field) => (
                    <TableHeader
                      key={field.key}
                      title={field.label}
                      className={`truncate ${columnWidth(field)}`}
                    >
                      {field.shortLabel ?? field.label}
                    </TableHeader>
                  ))}
                  <TableHeader className={`text-right ${ROW_WIDTH}`}>Row</TableHeader>
                </TableRow>
              </TableHead>

              {grouped.map(({ classification, section, changes }) => (
                <TableBody
                  key={classification}
                  aria-label={section.title}
                  hidden={!changes.some((change) => visible.has(change))}
                >
                  {changes.map((change) => {
                    const selectable = isSelectable(change);
                    const id = changeId(change);
                    const contested = isContested(change, selected, result);
                    const errors = errorsOf.get(change);
                    const rowErrors = (contested ? errors?.all : errors?.own) ?? {};
                    const hidden = !visible.has(change);
                    const invalid = hasNonDuplicateErrors(change);
                    const failure = failures?.[id];
                    // A skipped row stays, struck through, so an accidental skip is undoable.
                    const included = !selectable || selected.has(id);
                    const rowLabel = rowLabelFor(change, included, result);
                    return (
                      <React.Fragment key={id}>
                        <TableRow
                          data-line={change.line}
                          hidden={hidden}
                          // One background per row: a problem's tint replaces the section's.
                          className={`${included ? '' : 'line-through opacity-50'} ${
                            invalid
                              ? 'bg-red-50/60 outline outline-1 -outline-offset-1 outline-red-300 dark:bg-red-500/5 dark:outline-red-500/40'
                              : // Amber: the row is valid, waiting on the others to be skipped.
                                contested
                                ? 'bg-amber-50/60 outline outline-1 -outline-offset-1 outline-amber-300 dark:bg-amber-500/5 dark:outline-amber-500/40'
                                : section.background
                          }`}
                        >
                          {/* The cell's default padding is asymmetric, so it is
                              dropped to centre the control. */}
                          <TableCell className="!px-2 text-center align-middle leading-none">
                            {selectable && (
                              <button
                                type="button"
                                disabled={disabled}
                                onClick={() => toggle(id)}
                                aria-label={
                                  included
                                    ? `Skip this row, do not ${section.verb} it`
                                    : `Include this row again`
                                }
                                className="inline-flex rounded p-0.5 text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 disabled:hover:bg-transparent dark:hover:bg-neutral-700 dark:hover:text-neutral-200"
                              >
                                {included ? (
                                  <XMarkIcon className="size-4" />
                                ) : (
                                  <ArrowUturnLeftIcon className="size-4" />
                                )}
                              </button>
                            )}
                          </TableCell>
                          {schema.fields.map((field) => (
                            <ChangeCell
                              key={field.key}
                              change={change}
                              field={field}
                              messages={rowErrors[field.key]}
                              duplicateOnly={contested && !errors?.own[field.key]}
                              // A removal has no line: it comes from the existing data.
                              editable={!disabled && !!onCellChange && change.line !== undefined}
                              editCell={editCell}
                              leaveCell={leaveCell}
                              extensionLabel={
                                schema.fields.find((other) => other.key === field.extensionField)
                                  ?.label
                              }
                            />
                          ))}
                          <TableCell
                            title={typeof rowLabel.text === 'string' ? rowLabel.text : undefined}
                            className={`truncate text-right align-top tabular-nums ${ROW_WIDTH} ${rowLabel.className}`}
                          >
                            {rowLabel.text}
                          </TableCell>
                        </TableRow>
                        {failure && (
                          <TableRow hidden={hidden}>
                            <TableCell />
                            <TableCell
                              colSpan={schema.fields.length + 1}
                              className="font-medium text-red-700 dark:text-red-300"
                            >
                              {failure}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              ))}
            </Table>
          </Headless.PopoverGroup>
        </ScrollingIntoView.Provider>
      </div>
    </div>
  );
};
